import React, { useState, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import ModalWrapper from '../../../components/modals/ModalWrapper'
import ReauthenticationModal from '../../../components/reauthenticationModal/ReauthenticationModal'
import EmailVerificationCodeEntry from '../../../components/modals/emailVerification/EmailVerificationCodeEntry'
import EmailVerificationEmailPasswordForm from '../../../components/modals/emailVerification/EmailVerificationEmailPasswordForm'
import EmailVerificationBackupOffer from '../../../components/modals/emailVerification/EmailVerificationBackupOffer'
import EmailVerificationPrimaryOffer from '../../../components/modals/emailVerification/EmailVerificationPrimaryOffer'
import EmailVerificationAffirmPrimaryOffer from '../../../components/modals/emailVerification/EmailVerificationAffirmPrimaryOffer'
import EmailVerificationAffirmBackupOffer from '../../../components/modals/emailVerification/EmailVerificationAffirmBackupOffer'
import EmailVerificationAffirmBothOffer from '../../../components/modals/emailVerification/EmailVerificationAffirmBothOffer'
import EmailVerificationStepper from '../../../components/modals/emailVerification/EmailVerificationStepper'
import EmailVerificationEnvelopeIllustration from '../../../components/modals/emailVerification/EmailVerificationEnvelopeIllustration'
import {
  createEmailVerification,
  resendEmailVerification,
  verifyEmailVerification,
  dismissEmailVerificationPrompt,
  affirmPrimaryEmail,
  affirmBackupEmail,
  fetchEmailVerificationPrompts,
} from '../../../actions/emailVerification'
import getErrorMessageForRequest from '../../../utils/getErrorMessageForRequest'
import analytics from '../../../utils/cdpAnalytics'

// how long the "Verified!"/"Confirmed!"/etc. success state holds before the
// flow auto-advances (to the backup step, or by closing the modal)
const AUTO_ADVANCE_DELAY_MS = 4000

// Steps this container walks through, spanning Flow A (verify/update primary)
// and Flow B (add backup) from email_verification_banner.md:
//   verifyPrimaryOffer -> sendPrimaryCode -> primaryCodeEntry -> [newEmailForm -> newEmailCodeEntry]
//   -> backupOffer -> backupEmailForm -> backupCodeEntry
function EmailVerificationFlowContainer(props) {
  const {
    hideModal, flow, initialStep, user, history, signinMethods,
    createEmailVerification, resendEmailVerification, verifyEmailVerification,
    dismissEmailVerificationPrompt, affirmPrimaryEmail, affirmBackupEmail,
    fetchEmailVerificationPrompts,
  } = props

  const [step, setStep] = useState(initialStep || (flow === 'addBackup' ? 'backupOffer' : 'verifyPrimaryOffer'))
  // newEmailForm is reachable from three different steps (the primary offer, the
  // "I can't get this code" link, and backupOffer's "that's not my email") — track
  // whichever one we came from so its back link returns to the right place.
  const [newEmailFormOrigin, setNewEmailFormOrigin] = useState('verifyPrimaryOffer')
  const [verification, setVerification] = useState(null) // { id, email }
  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [formError, setFormError] = useState('')
  const [dismissLoading, setDismissLoading] = useState(false)
  // { purpose, email, nextStep } for whichever email-form submission drew a
  // 403 reauth_required, so the reauth modal's result can resubmit it
  const [pendingReauthSubmission, setPendingReauthSubmission] = useState(null)
  const [showReauthModal, setShowReauthModal] = useState(false)
  // tracks whether backup is being asked for right after a just-completed
  // verify/update (vs. reached standalone) — gates the stepper (see showStepper)
  const [justVerifiedPrimary, setJustVerifiedPrimary] = useState(false)
  // true when backupEmailForm/backupCodeEntry were reached via affirmBackupOffer's
  // "that's not my backup email" (replacing an already-verified backup), rather
  // than a fresh add — changes the code-entry copy and drops its back link
  const [replacingBackupEmail, setReplacingBackupEmail] = useState(false)
  // shown under the code-entry button for a beat before chaining straight into
  // the backup email form, so that transition doesn't feel like an abrupt swap
  const [verifiedMessage, setVerifiedMessage] = useState('')
  const verifiedTimeoutRef = useRef(null)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [affirmError, setAffirmError] = useState('')

  useEffect(() => () => {
    if (verifiedTimeoutRef.current) clearTimeout(verifiedTimeoutRef.current)
  }, [])

  // this modal can be showing (independently) in more than one tab at once —
  // closing it here (verified, skipped, remind later, etc.) should also
  // close it in any other tab that has it open, not just this one
  function closeModal() {
    hideModal({ onlyIfModal: 'EMAIL_VERIFICATION_FLOW_MODAL', syncActionBC: true })
  }

  function goToStep(nextStep) {
    setFormError('')
    setStep(nextStep)
  }

  const goToNewEmailForm = () => {
    setNewEmailFormOrigin(step)
    goToStep('newEmailForm')
  }

  const proceedToAddBackupAfterDelay = (message) => {
    setVerifiedMessage(message)
    verifiedTimeoutRef.current = setTimeout(() => {
      setVerification(null)
      setJustVerifiedPrimary(true)
      setVerifiedMessage('')
      // skip the standalone offer screen's copy — they just verified, so go
      // straight to collecting the backup address
      goToStep('backupEmailForm')
    }, AUTO_ADVANCE_DELAY_MS)
  }

  const proceedToAffirmBackupAfterDelay = (message) => {
    setVerifiedMessage(message)
    verifiedTimeoutRef.current = setTimeout(() => {
      setVerification(null)
      setVerifiedMessage('')
      goToStep('affirmBackupOffer')
    }, AUTO_ADVANCE_DELAY_MS)
  }

  const closeModalAfterDelay = (message) => {
    setVerifiedMessage(message)
    verifiedTimeoutRef.current = setTimeout(() => {
      setVerifiedMessage('')
      closeModal()
    }, AUTO_ADVANCE_DELAY_MS)
  }

  // shared by every "what's next" decision point: a full add-backup ask wins
  // over a stale-backup affirm, and if neither is due, fall back (e.g. close
  // right away, or close after showing a message)
  const chainFromPromptsOrFallback = (prompts, message, fallback) => {
    if (prompts && prompts.addBackup) {
      proceedToAddBackupAfterDelay(message)
    } else if (prompts && prompts.affirmBackup && user.backupEmail) {
      proceedToAffirmBackupAfterDelay(message)
    } else {
      fallback()
    }
  }

  const sendPrimaryCode = async () => {
    setLoading(true)
    setLoadError('')
    try {
      const response = await createEmailVerification(user.id, { purpose: 'verifyPrimary' })
      setVerification({
        id: response.id, email: user.email, resendAvailableAt: response.resendAvailableAt, purpose: 'verifyPrimary',
      })
      setLoading(false)
      setStep('primaryCodeEntry')
    } catch (err) {
      setLoading(false)
      setLoadError(getErrorMessageForRequest({ requestName: 'createEmailVerification', response: err.response }))
    }
  }

  // only run for the initial step, on mount
  useEffect(() => {
    if (step === 'sendPrimaryCode') {
      sendPrimaryCode()
    }
  }, [])

  const handleVerifyPrimaryCode = async (code) => {
    try {
      const { purpose } = verification
      const response = await verifyEmailVerification(user.id, verification.id, code, purpose)
      analytics.track('Email verification flow action', { flow: 'verifyPrimary', action: 'verified' })
      chainFromPromptsOrFallback(response.prompts, 'Verified!', () => closeModalAfterDelay('Verified!'))
    } catch (err) {
      throw new Error(getErrorMessageForRequest({ requestName: 'verifyEmailVerificationCode', response: err.response }))
    }
  }

  // shared by both email-collection steps: sends the create request, and on a
  // 403 reauth_required stashes what was being submitted and opens the general
  // reauth modal (password, or Google/Apple idToken) instead of failing outright
  const submitEmailVerificationRequest = async ({
    purpose, email, nextStep, authentication,
  }) => {
    setLoading(true)
    setFormError('')
    try {
      const response = await createEmailVerification(user.id, { purpose, email, ...authentication })
      setLoading(false)
      setPendingReauthSubmission(null)
      setVerification({
        id: response.id, email, resendAvailableAt: response.resendAvailableAt, purpose,
      })
      setStep(nextStep)
    } catch (err) {
      setLoading(false)
      if (err.response && err.response.status === 403) {
        setPendingReauthSubmission({ purpose, email, nextStep })
        setShowReauthModal(true)
        return
      }
      setFormError(getErrorMessageForRequest({ requestName: 'createEmailVerification', response: err.response, data: { email } }))
    }
  }

  const handleReauthenticate = (authentication) => {
    if (!pendingReauthSubmission) { return }
    submitEmailVerificationRequest({ ...pendingReauthSubmission, authentication })
  }

  const handleSubmitNewEmail = ({ email }) => submitEmailVerificationRequest({
    purpose: 'updatePrimary', email, nextStep: 'newEmailCodeEntry',
  })

  const handleVerifyNewEmailCode = async (code) => {
    try {
      const { purpose, email } = verification
      const response = await verifyEmailVerification(user.id, verification.id, code, purpose, email)
      analytics.track('Email verification flow action', { flow: 'verifyPrimary', action: 'updated' })
      chainFromPromptsOrFallback(response.prompts, 'Account email updated!', () => closeModalAfterDelay('Account email updated!'))
    } catch (err) {
      throw new Error(getErrorMessageForRequest({ requestName: 'verifyEmailVerificationCode', response: err.response }))
    }
  }

  const handleSubmitBackupEmail = ({ email }) => submitEmailVerificationRequest({
    purpose: 'upsertBackup', email, nextStep: 'backupCodeEntry',
  })

  const handleVerifyBackupCode = async (code) => {
    try {
      const { purpose, email } = verification
      await verifyEmailVerification(user.id, verification.id, code, purpose, email)
      analytics.track('Email verification flow action', { flow: 'addBackup', action: 'verified' })
      closeModalAfterDelay('Backup email added!')
    } catch (err) {
      throw new Error(getErrorMessageForRequest({ requestName: 'verifyEmailVerificationCode', response: err.response }))
    }
  }

  const handleResend = async () => {
    try {
      return await resendEmailVerification(user.id, verification.id)
    } catch (err) {
      throw new Error(getErrorMessageForRequest({ requestName: 'resendEmailVerification', response: err.response }))
    }
  }

  // shared by backupOffer (fresh add) and backupEmailForm (fresh add, or
  // replacing an existing backup via affirmBackupOffer) — dismiss whichever
  // prompt is actually due for the current context
  const handleSkipBackup = () => {
    const promptName = replacingBackupEmail ? 'affirmBackup' : 'addBackup'
    setDismissLoading(true)
    dismissEmailVerificationPrompt(user.id, promptName).finally(() => {
      setDismissLoading(false)
      closeModal()
    })
  }

  const handleNeverAskBackup = () => {
    setDismissLoading(true)
    analytics.track('Email verification flow action', { flow: 'addBackup', action: 'neverAskAgain' })
    dismissEmailVerificationPrompt(user.id, 'addBackup', { never: true }).finally(() => {
      setDismissLoading(false)
      closeModal()
    })
  }

  const handleRemindPrimaryLater = () => {
    setDismissLoading(true)
    analytics.track('Email verification flow action', { flow: 'verifyPrimary', action: 'remindLater' })
    dismissEmailVerificationPrompt(user.id, 'verifyPrimary').finally(() => {
      setDismissLoading(false)
      closeModal()
    })
  }

  const handleRemindAffirmLater = () => {
    setDismissLoading(true)
    analytics.track('Email verification flow action', { flow: 'affirmPrimary', action: 'remindLater' })
    dismissEmailVerificationPrompt(user.id, 'affirmPrimary').finally(() => {
      setDismissLoading(false)
      closeModal()
    })
  }

  const handleAffirmPrimary = async () => {
    setConfirmLoading(true)
    setAffirmError('')
    try {
      await affirmPrimaryEmail(user.id)
      analytics.track('Email verification flow action', { flow: 'affirmPrimary', action: 'confirmed' })
      const refreshedPrompts = await fetchEmailVerificationPrompts(user.id)
      setConfirmLoading(false)
      chainFromPromptsOrFallback(refreshedPrompts, 'Confirmed!', () => closeModalAfterDelay('Confirmed!'))
    } catch (err) {
      setConfirmLoading(false)
      setAffirmError(getErrorMessageForRequest({ requestName: 'affirmPrimaryEmail', response: err.response }))
    }
  }

  const handleRemindAffirmBackupLater = () => {
    setDismissLoading(true)
    analytics.track('Email verification flow action', { flow: 'affirmBackup', action: 'remindLater' })
    dismissEmailVerificationPrompt(user.id, 'affirmBackup').finally(() => {
      setDismissLoading(false)
      closeModal()
    })
  }

  const handleAffirmBackup = async () => {
    setConfirmLoading(true)
    setAffirmError('')
    try {
      await affirmBackupEmail(user.id)
      analytics.track('Email verification flow action', { flow: 'affirmBackup', action: 'confirmed' })
      setConfirmLoading(false)
      closeModalAfterDelay('Confirmed!')
    } catch (err) {
      setConfirmLoading(false)
      setAffirmError(getErrorMessageForRequest({ requestName: 'affirmBackupEmail', response: err.response }))
    }
  }

  const handleRemindAffirmBothLater = () => {
    setDismissLoading(true)
    analytics.track('Email verification flow action', { flow: 'affirmBoth', action: 'remindLater' })
    Promise.all([
      dismissEmailVerificationPrompt(user.id, 'affirmPrimary'),
      dismissEmailVerificationPrompt(user.id, 'affirmBackup'),
    ]).finally(() => {
      setDismissLoading(false)
      closeModal()
    })
  }

  // both are lightweight "just confirm" affirms, so a single click fires
  // them concurrently instead of walking through two sequential screens
  const handleAffirmBoth = async () => {
    setConfirmLoading(true)
    setAffirmError('')
    try {
      await Promise.all([affirmPrimaryEmail(user.id), affirmBackupEmail(user.id)])
      analytics.track('Email verification flow action', { flow: 'affirmBoth', action: 'confirmed' })
      setConfirmLoading(false)
      closeModalAfterDelay('Confirmed!')
    } catch (err) {
      setConfirmLoading(false)
      setAffirmError(getErrorMessageForRequest({ requestName: 'affirmPrimaryEmail', response: err.response }))
    }
  }

  const handleOpenAccountPage = () => {
    analytics.track('Email verification flow action', { flow: 'affirmBoth', action: 'updateEmails' })
    closeModal()
    history.push('/account')
  }

  function backupEmailFormDescription() {
    if (replacingBackupEmail) {
      return "We'll send a code to your new email address to complete the update."
    }
    return justVerifiedPrimary ?
      'Your account email is verified - a backup covers you if you ever lose access to your account email.' :
      'A backup covers you if you ever lose access to your account email.'
  }

  function renderContent() {
    switch (step) {
      case 'verifyPrimaryOffer':
        return (
          <EmailVerificationPrimaryOffer
            email={user.email}
            onSendCode={sendPrimaryCode}
            onDifferentEmail={goToNewEmailForm}
            onRemindLater={handleRemindPrimaryLater}
            remindLoading={dismissLoading}
          />
        )

      case 'affirmPrimaryOffer':
        return (
          <EmailVerificationAffirmPrimaryOffer
            email={user.email}
            onAffirm={handleAffirmPrimary}
            onDifferentEmail={goToNewEmailForm}
            onRemindLater={handleRemindAffirmLater}
            remindLoading={dismissLoading}
            confirmLoading={confirmLoading}
            confirmedMessage={verifiedMessage}
            errorMessage={affirmError}
          />
        )

      case 'affirmBackupOffer':
        return (
          <EmailVerificationAffirmBackupOffer
            email={user.backupEmail}
            onAffirm={handleAffirmBackup}
            onDifferentEmail={() => { setReplacingBackupEmail(true); goToStep('backupEmailForm') }}
            onRemindLater={handleRemindAffirmBackupLater}
            remindLoading={dismissLoading}
            confirmLoading={confirmLoading}
            confirmedMessage={verifiedMessage}
            errorMessage={affirmError}
          />
        )

      case 'affirmBothOffer':
        return (
          <EmailVerificationAffirmBothOffer
            primaryEmail={user.email}
            backupEmail={user.backupEmail}
            onAffirm={handleAffirmBoth}
            onUpdateEmails={handleOpenAccountPage}
            onRemindLater={handleRemindAffirmBothLater}
            remindLoading={dismissLoading}
            confirmLoading={confirmLoading}
            confirmedMessage={verifiedMessage}
            errorMessage={affirmError}
          />
        )

      case 'sendPrimaryCode':
        return (
          <div className='emailVerificationFlow-loading'>
            {loading && (
              <div className='emailVerificationFlow-loading-message'>Sending code to your email...</div>
            )}
            {loadError && (
              <React.Fragment>
                <div className='emailVerificationFlow-error'>{loadError}</div>
                <button type='button' className='btn btn--blue' onClick={sendPrimaryCode}>Try again</button>
                <button
                  type='button'
                  className='emailVerificationFlow-linkBtn'
                  onClick={() => goToStep('verifyPrimaryOffer')}
                >
                  ← Back
                </button>
              </React.Fragment>
            )}
          </div>
        )

      case 'primaryCodeEntry':
        return (
          <EmailVerificationCodeEntry
            email={(verification && verification.email) || user.email}
            title='Please verify your email address'
            description='Enter the code we sent to your email'
            resendAvailableAt={verification && verification.resendAvailableAt}
            verifiedMessage={verifiedMessage}
            onSubmitCode={handleVerifyPrimaryCode}
            onResend={handleResend}
            onBack={goToNewEmailForm}
            backLabel="I can't get this code"
          />
        )

      case 'newEmailForm':
        return (
          <EmailVerificationEmailPasswordForm
            title='Update account email'
            description="Don't have access to the account email anymore? Update now. We'll send a code to your new email address to complete the change."
            emailLabel='New email address'
            submitLabel='Send code'
            currentEmail={user.email}
            submitLoading={loading}
            errorMessage={formError}
            onSubmit={handleSubmitNewEmail}
            onBack={() => goToStep(newEmailFormOrigin)}
          />
        )

      case 'newEmailCodeEntry':
        return (
          <EmailVerificationCodeEntry
            email={verification && verification.email}
            title='Verify your new email'
            description="We've sent a code to your new email address, please enter the code to finish updating your Plickers email"
            resendAvailableAt={verification && verification.resendAvailableAt}
            verifiedMessage={verifiedMessage}
            onSubmitCode={handleVerifyNewEmailCode}
            onResend={handleResend}
            onBack={goToNewEmailForm}
            backLabel='Change email'
          />
        )

      case 'backupOffer':
        return (
          <EmailVerificationBackupOffer
            onAddBackup={() => goToStep('backupEmailForm')}
            onSkip={handleSkipBackup}
            onNeverAskAgain={handleNeverAskBackup}
            skipLoading={dismissLoading}
          />
        )

      case 'backupEmailForm':
        return (
          <EmailVerificationEmailPasswordForm
            title={replacingBackupEmail ? 'Update backup email' : 'Add a backup email'}
            description={backupEmailFormDescription()}
            showIllustration={false}
            emailLabel='Backup email address'
            submitLabel='Add a backup'
            currentEmail={user.email}
            duplicateEmailError='Please enter a different email than your account email.'
            submitLoading={loading}
            errorMessage={formError}
            onSubmit={handleSubmitBackupEmail}
            onSkip={handleSkipBackup}
            skipLoading={dismissLoading}
          />
        )

      case 'backupCodeEntry':
        return (
          <EmailVerificationCodeEntry
            email={verification && verification.email}
            title={replacingBackupEmail ? 'Verify your new backup email' : 'Verify your backup email'}
            description='Enter the code we sent to your email'
            resendAvailableAt={verification && verification.resendAvailableAt}
            showIllustration={false}
            verifiedMessage={verifiedMessage}
            onSubmitCode={handleVerifyBackupCode}
            onResend={handleResend}
            onBack={replacingBackupEmail ? undefined : () => goToStep('backupEmailForm')}
            backLabel='Change email'
          />
        )

      default:
        return null
    }
  }

  const onBackupSteps = ['backupOffer', 'backupEmailForm', 'backupCodeEntry'].includes(step)
  // the chained verify-primary -> add-backup journey gets the 2-step progress
  // stepper; reaching these same steps standalone (primary already verified,
  // e.g. the addBackup entry point) gets a plain verified-email icon instead —
  // there's no "step 1" to show progress against, just a confirmation of why
  // they're being asked
  const showStepper = justVerifiedPrimary && onBackupSteps
  const showVerifiedIcon = !justVerifiedPrimary && onBackupSteps

  return (
    <div className='emailVerificationFlow-modalContainer marketing'>
      <ModalWrapper
        // dismiss must be explicit (Remind me later / Skip / Not now), not a
        // stray click on the backdrop or the (otherwise decorative) close icon
        hideModal={() => {}}
        closeBtn={false}
        showModalHeader={false}
        modalStyle='dark'
      >
        <div className='emailVerificationFlow-modalContent'>
          {showStepper && <EmailVerificationStepper backupVerified={!!verifiedMessage} />}
          {showVerifiedIcon && <EmailVerificationEnvelopeIllustration />}
          {renderContent()}
        </div>
      </ModalWrapper>
      {showReauthModal && (
        <ReauthenticationModal
          title={pendingReauthSubmission && pendingReauthSubmission.purpose === 'updatePrimary' ? 'Update Account Email' : 'Add a Backup Email'}
          signinMethodsToDisplay={signinMethods}
          onAuthenticate={handleReauthenticate}
          hideModal={() => setShowReauthModal(false)}
        />
      )}
    </div>
  )
}

export default withRouter(connect(
  (state) => ({
    user: state.user,
    signinMethods: state.signinMethods,
  }),
  {
    createEmailVerification,
    resendEmailVerification,
    verifyEmailVerification,
    dismissEmailVerificationPrompt,
    affirmPrimaryEmail,
    affirmBackupEmail,
    fetchEmailVerificationPrompts,
  },
)(EmailVerificationFlowContainer))
